import React, { Component } from 'react';
import {
  MACRO_WIZARD,
  SEARCH_ALL,
  SEARCH_KB,
  SEARCH_MACROS,
  SEARCH_ORDERS,
  SEARCH_TICKETS,
} from '../../../constants/searchTypes';
import { ALL_ALERTS } from 'constants/alerts';
import { BlueSection } from './Search.styles';
import SearchTitle from './SearchTitle';
import styled from 'styled-components/macro';
import {
  getSearchTypeOfSelectedAction,
  getAlertManagerType,
  getCurrentBlockingAlert,
  getSimplrTaggingActive,
  extractDecisionTreeOfSelectedTicket,
  extractCurrentMacroWizardPath,
  extractMacroListByPath,
  extractNonHistoricalTicketIdFromState,
  getExtraBusinessInfoOfSelectedTicket,
  extractSimplrTagsForSelectedTicket,
  extractSelectedTicketFromState,
  isSelectedTicketReviewTicket,
  extractDecisionFlowsData,
} from '../../../extractFromState';
import { shouldShowSearchButtons } from '../../../selectors/tools';
import { connect } from 'react-redux';
import SearchOrders from './SearchOrders/SearchOrders';
import SearchButtons from './SearchButtons';
import SearchAll from './SearchAll';
import Alerts from 'components/tools/alerts/Alerts';
import AllAlerts from 'components/tools/alerts/AllAlerts/AllAlerts';
import BlockingAlert from 'components/tools/alerts/BlockingAlert/BlockingAlert';
import DTBreadcrumb from '../../DecisionTree/Common/DTBreadcrumb/DTBreadcrumb';
import LoadingComponent from 'components/LoadingComponent/LoadingComponent';
import DecisionTree from '../../DecisionTree/DecisionTree';
import LogicFlowWebIframe from './LogicFlowWeb/LogicFlowWebIframe';
import { handleError } from 'services/ErrorService/ErrorService';
import { CopilotWidget } from '@asurion-core/copilot-widget';
import { getMessagesInConversation } from 'util/messageUtil';
import { INBOUND, OUTBOUND } from 'constants/messageDirection';
import { copyMacroTextAction } from 'actions';
import environment from 'environment';

const env = environment.nodeEnv === 'production' ? 'prod' : 'dev';
const serverUrl = new URL(environment.app.serverApiUrl);
const chatServerURL = serverUrl.protocol + '//' + serverUrl.hostname;

class SearchPage extends Component {
  renderComponent() {
    const {
      searchType,
      alertManagerType,
      blockingAlert,
      macroWizardTagPath,
      selectedTicketId,
      // CopilotWidget props
      copilotWidgetEnabled,
      transcript,
      externalTicketId,
      moreTicketInfo,
    } = this.props;
    // separate because it should not interact
    // with the selected searchType
    if (!!blockingAlert) {
      return <BlockingAlert />;
    } else if (!!alertManagerType) {
      return alertManagerType === ALL_ALERTS ? <AllAlerts /> : <Alerts />;
    }
    if (this.shouldRenderLogicFlowWebInfo()) {
      return (
        <>
          {copilotWidgetEnabled && (
            <div style={{ height: '100%', position: 'absolute', right: 0, top: 0 }}>
              <CopilotWidget
                externalTicketId={externalTicketId}
                customFlowSystemId=""
                transcript={transcript}
                widgetId={'copilot'}
                moreTicketInfo={moreTicketInfo}
                salesforceService={{
                  salesforceBackendUrl: chatServerURL,
                }}
                asurionChatService={{
                  asurionChatServerUrl: chatServerURL,
                }}
                currentEnv={env}
                publicUrl={chatServerURL}
                publicServerDomain={chatServerURL}
                publicServerUrlPrefix="/chat-api"
                mixpanelToken={environment?.copilot?.mixpanelToken}
                startWidgetExpanded
                sentry={{ enabled: false }}
                fullStory={{ isDevMode: true }}
                showStateDebugger
                copyMacro={(text) => this.props.dispatch(copyMacroTextAction(text))}
              />
            </div>
          )}
          <LogicFlowWebIframe macroWizardTagPath={macroWizardTagPath} conversationId={selectedTicketId} />
        </>
      );
    }
    switch (searchType) {
      case SEARCH_ORDERS:
        return <SearchOrders />;
      case SEARCH_ALL:
      case SEARCH_KB:
      case SEARCH_TICKETS:
      case SEARCH_MACROS:
        return <SearchAll />;
      case MACRO_WIZARD:
        return <DecisionTree />;
      default:
        return (
          <LoadingContainer>
            <LoadingComponent delay={0} marginTop={'0px'} />
          </LoadingContainer>
        );
    }
  }

  shouldRenderLogicFlowWebInfo() {
    const { macroWizardTagPath, decisionTree, macroWizardList, extraBusinessInfo, simplrTags, decisionFlows } =
      this.props;
    const isExpertLogicFlowEnabled = !!extraBusinessInfo?.expertLogicFlow?.enabled;

    if (!isExpertLogicFlowEnabled) {
      return false;
    }

    /**
     * Guards against and warns of possibility where the partner account has no simplrTags, its the tag rules,
     * or macroWizardList and prevents locking up the macro wizard for both Decision Tree and Decision Flows.
     *
     * This is an unlikely scenario but will give us warning that we are missing partner data.
     */
    if (!simplrTags || !simplrTags.categoryRules || !macroWizardList) {
      handleError({
        customMessage: 'Error: partner account is missing tags and its category rules or list of macros',
        customFields: { simplrTags, macroWizardList },
      });

      return false;
    }

    if (macroWizardTagPath === undefined) {
      handleError({
        customMessage: 'Error: invalid macro wizard tag path was passed',
      });

      return false;
    }

    // Named conditionals for clarity on priority checks required for rendering either a tree or flow.
    const isCompleteTagPath = macroWizardTagPath?.includes('--');
    const isOutOfScope =
      simplrTags.categoryRules?.[macroWizardTagPath] === false && macroWizardList[macroWizardTagPath]?.length === 0;
    const isMacroAssociatedWithTag = macroWizardList[macroWizardTagPath]?.length;
    const isDecisionTreeAssociatedWithTag = decisionTree[macroWizardTagPath];
    const doesDecisionFlowExist = !!decisionFlows[macroWizardTagPath]?.flowId;

    return (
      isCompleteTagPath &&
      !isOutOfScope &&
      !isDecisionTreeAssociatedWithTag &&
      !isMacroAssociatedWithTag &&
      doesDecisionFlowExist
    );
  }

  render() {
    return (
      <SearchPanelWrapper>
        <Header>
          <SearchTitle />
          {this.props.shouldShowSearchButtons && !this.props.taggingActive && (
            <BlueSection id={'searchHeader'}>
              <SearchButtons />
            </BlueSection>
          )}
          {!this.shouldRenderLogicFlowWebInfo() && <DTBreadcrumb />}
        </Header>
        {this.renderComponent()}
      </SearchPanelWrapper>
    );
  }
}

function mapStateToProps(state) {
  const selectedTicket = extractSelectedTicketFromState(state);
  const conversation = getMessagesInConversation(
    selectedTicket,
    state.conversation,
    state.user,
    isSelectedTicketReviewTicket(state)
  );
  const transcript = conversation
    .filter(
      ({ direction, messageBody }) =>
        [OUTBOUND, INBOUND].includes(direction) &&
        messageBody !== 'User starting new ticket' &&
        messageBody !== 'escalating'
    )
    .map((message) => ({
      text: message.messageBody,
      sender: message.direction === INBOUND ? 'USER' : 'AGENT',
      id: message.messageId,
    }));

  return {
    searchType: getSearchTypeOfSelectedAction(state),
    shouldShowSearchButtons: shouldShowSearchButtons(state),
    taggingActive: getSimplrTaggingActive(state),
    alertManagerType: getAlertManagerType(state),
    blockingAlert: getCurrentBlockingAlert(state),
    decisionTree: extractDecisionTreeOfSelectedTicket(state),
    macroWizardTagPath: extractCurrentMacroWizardPath(state),
    macroWizardList: extractMacroListByPath(state),
    selectedTicketId: extractNonHistoricalTicketIdFromState(state),
    extraBusinessInfo: getExtraBusinessInfoOfSelectedTicket(state),
    simplrTags: extractSimplrTagsForSelectedTicket(state),
    copilotWidgetEnabled: state.copilotWidget?.enabled,
    transcript,
    externalTicketId: selectedTicket?.ticketId,
    moreTicketInfo: state.conversation?.moreTicketInfo,
    decisionFlows: extractDecisionFlowsData(state),
  };
}

export default SearchPage = connect(mapStateToProps)(SearchPage);

const SearchPanelWrapper = styled.div`
  display: grid;
  height: 100%;
  overflow: hidden;
  grid-template-rows: min-content 1fr;
`;

const Header = styled.div``;

const LoadingContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
